import { BADGE_VARIANT } from "../../constants"

export const DEVICE_INFO_TITLE = "Device Info"
export const SEARCH_DEBOUNCE_DELAY = 400

export const PLAYBACK_BUFFERING_LABEL = "Buffering recording…"
export const PLAYBACK_LOADING_STREAM_LABEL = "Loading stream…"
export const PLAYBACK_EMPTY_STATE_DEFAULT = "Select a recording segment on the timeline to start playback"
export const PLAYBACK_BADGE_LABEL = "PLAYBACK"
export const PLAYBACK_ONLINE_BADGE_LABEL = "ONLINE"
export const PLAYBACK_CAMERA_TITLE_FALLBACK = "Collaboration Zone"
export const PLAYBACK_CAMERA_LOCATION_LABEL = "Meeting Room"
export const PLAYBACK_REWIND_TITLE = "Rewind"
export const PLAYBACK_PLAY_TITLE = "Play"
export const PLAYBACK_PAUSE_TITLE = "Pause"
export const PLAYBACK_FAST_FORWARD_TITLE = "Fast Forward"
export const PLAYBACK_FULL_SCREEN_TITLE = "Full Screen"

/** Device type identifiers */
export const DEVICE_TYPES = {
  CAMERA: "camera",
  HUB: "hub",
  NVR: "nvr",
  NVR_CAMERA: "nvr_camera",
  Edit_HUB: "Edit HUB",
  Edit_NVR_CAMERA: "Edit NVR Camera",
  Edit_CAMERA: "Edit Camera",
}

export const DEVICE_LOCATION = {
  FLOOR: "floor",
  SPACE: "space",
  SITE: "site",
}

/** Dropdown options for the device-type selector */
export const DEVICE_OPTIONS = [
  { value: DEVICE_TYPES.CAMERA, label: "Camera" },
  { value: DEVICE_TYPES.HUB, label: "Hub" },
  { value: DEVICE_TYPES.NVR, label: "NVR" },
  { value: DEVICE_TYPES.NVR_CAMERA, label: "NVR Camera" },
]

/** Status type identifiers */
export const STATUS_TYPES = {
  ONLINE: "online",
  OFFLINE: "offline",
  ALERT: "alert",
  NEW: "new",
}

/** Dropdown options for the status selector */
export const STATUS_OPTIONS = [
  { value: STATUS_TYPES.ONLINE, label: "Online" },
  { value: STATUS_TYPES.OFFLINE, label: "Offline" },
]

/** Notification severity identifiers */
export const NOTIFICATION_SEVERITY = {
  INFO: "info",
  WARNING: "warning",
  CRITICAL: "critical",
}

/** Severity → Badge variant */
export const NOTIFICATION_SEVERITY_VARIANT = {
  [NOTIFICATION_SEVERITY.INFO]: BADGE_VARIANT.PRIMARY,
  [NOTIFICATION_SEVERITY.WARNING]: BADGE_VARIANT.WARNINGDARK,
  [NOTIFICATION_SEVERITY.CRITICAL]: BADGE_VARIANT.ERROR,
}

/** Dropdown options for the Severity filter (match the grid values) */
export const NOTIFICATION_SEVERITY_OPTIONS = [
  { value: NOTIFICATION_SEVERITY.INFO, label: "Info" },
  { value: NOTIFICATION_SEVERITY.WARNING, label: "Warning" },
  { value: NOTIFICATION_SEVERITY.CRITICAL, label: "Critical" },
]

/** Notification status identifiers */
export const NOTIFICATION_STATUS = {
  OPEN: "open",
  ACKNOWLEDGED: "acknowledged",
  RESOLVED: "resolved",
  FALSE_POSITIVE: "false positive",
}

/** Status → Badge variant */
export const NOTIFICATION_STATUS_VARIANT = {
  [NOTIFICATION_STATUS.OPEN]: BADGE_VARIANT.WARNING,
  [NOTIFICATION_STATUS.ACKNOWLEDGED]: BADGE_VARIANT.WARNINGDARK,
  [NOTIFICATION_STATUS.RESOLVED]: BADGE_VARIANT.SUCCESS,
  [NOTIFICATION_STATUS.FALSE_POSITIVE]: BADGE_VARIANT.ERROR,
}

/** Dropdown options for the Status filter (match the grid values) */
export const NOTIFICATION_STATUS_OPTIONS = [
  { value: NOTIFICATION_STATUS.OPEN, label: "Open" },
  { value: NOTIFICATION_STATUS.ACKNOWLEDGED, label: "Acknowledged" },
  { value: NOTIFICATION_STATUS.RESOLVED, label: "Resolved" },
  { value: NOTIFICATION_STATUS.FALSE_POSITIVE, label: "False Positive" },
]

/**
 * Dropdown options for updating an event's status (no "Open" — events can
 * only move forward)
 */
export const NOTIFICATION_STATUS_UPDATE_OPTIONS =
  NOTIFICATION_STATUS_OPTIONS.filter(
    (option) => option.value !== NOTIFICATION_STATUS.OPEN,
  )

/**
 * Status → check icon color.
 * Mirrors the status Badge text color so the row's check icon matches its
 * status label (see NOTIFICATION_STATUS_VARIANT / BADGE_VARIANT_STYLES).
 */
export const NOTIFICATION_STATUS_COLOR = {
  [NOTIFICATION_STATUS.OPEN]: "var(--secondary-500)",
  [NOTIFICATION_STATUS.RESOLVED]: "var(--success-alert-700)",
  [NOTIFICATION_STATUS.ACKNOWLEDGED]: "var(--primary2-500)",
  [NOTIFICATION_STATUS.FALSE_POSITIVE]: "var(--primary2-500)",
}

export const NOTIFICATION_STATUS_DEFAULT_COLOR = "var(--secondary-500)"

/** Check-badge type identifiers */
export const CHECK_TYPE = {
  VERIFIED: "verified",
  PENDING: "pending",
  FAILED: "failed",
}

/** Check type → badge icon color */
export const CHECK_TYPE_COLOR = {
  [CHECK_TYPE.VERIFIED]: "#0DA709",
  [CHECK_TYPE.PENDING]: "#F58220",
  [CHECK_TYPE.FAILED]: "#727272",
}

export const CHECK_TYPE_DEFAULT_COLOR = "#727272"

/** Permission keys for Role-Based Access Control (RBAC) */
export const PERMISSION_KEYS = {
  VIDEO_LIVE: "video.live",
  VIDEO_PLAYBACK: "video.playback",
  EVENTS_VIEW_OPERATIONAL: "events.view.operational",
  EVENTS_VIEW_AI: "events.view.ai",
  EVENTS_ACKNOWLEDGE: "events.acknowledge",
  EVENTS_RESOLVE: "events.resolve",
  EVENTS_FALSE_POSITIVE: "events.false_positive",
  ANALYTICS_VIEW: "analytics.view",
  DEVICES_VIEW: "devices.view",
  DEVICES_MANAGE: "devices.manage",
  USERS_INVITE: "users.invite",
  USERS_MANAGE: "users.manage",
  AUDIT_VIEW_ALL: "audit.view.all",
  AUDIT_VIEW_DEVICES: "audit.view.devices",
  BILLING_VIEW: "billing.view",
  BILLING_MANAGE: "billing.manage",
  CLIP_EXPORT: "clip.export",
  ORG_SETTINGS: "org.settings",
}
